import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, RefreshCcw, Server, Search, Copy, Check, Eye, X } from 'lucide-react';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams } from 'react-router-dom';

interface LogRow {
  id: string;
  courierId: number;
  courierCode: string;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  merchantId?: number | null;
  statusCode?: number | null;
  errorMessage?: string | null;
  requestUrl?: string | null;
  requestBody?: any;
  responseBody?: any;
  createdAt: string;
  isError?: boolean;
  courierName?: string | null;
  merchantName?: string | null;
}

interface Courier { id: number; name: string; code: string }

/** Pretty-print any payload: objects as indented JSON, JSON-looking strings re-parsed first. */
function pretty(v: any): string {
  if (v == null) return '(empty)';
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.startsWith('{') || t.startsWith('[')) {
      try { return JSON.stringify(JSON.parse(t), null, 2); } catch { /* not JSON — show raw */ }
    }
    return v;
  }
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

export function CourierApiLogsPage() {
  const { user } = useAuth();
  const isAdminOrOps = user?.kind === 'user' && ['SUPER_ADMIN', 'ADMIN', 'OPERATION'].includes(user.userType ?? '');
  if (!isAdminOrOps) return <Navigate to="/dashboard" replace />;

  // Deep links land here with a pre-set entity (e.g. Pickup Requests → "log").
  const [sp] = useSearchParams();
  const [courierId, setCourierId] = useState<number | ''>('');
  const [action, setAction] = useState<string>('');
  const [entityId, setEntityId] = useState<string>(sp.get('entityId') ?? '');
  // Free-text search over the courier's error message (also matches the request URL / action).
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorQuery, setErrorQuery] = useState<string>('');
  const [onlyErrors, setOnlyErrors] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [detail, setDetail] = useState<LogRow | null>(null);

  const { data: couriers } = useQuery({ queryKey: ['couriers', 'active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data });
  // Actions come from a distinct query, so the dropdown lists every action ever logged rather than
  // only the ones that happen to appear on the current page.
  const { data: actionOpts = [] } = useQuery({
    queryKey: ['courier-api-log-actions'],
    queryFn: async () => (await api.get<string[]>('/courier-api-logs/actions')).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['courier-api-logs', courierId, action, entityId, errorQuery, onlyErrors, page, pageSize],
    queryFn: async () => (await api.get<{ total: number; page: number; pageSize: number; items: LogRow[] }>('/courier-api-logs', {
      params: {
        courierId: courierId || undefined,
        action: action || undefined,
        entityId: entityId || undefined,
        errorMessage: errorQuery || undefined,
        isError: onlyErrors || undefined,
        page, pageSize,
      },
    })).data,
    placeholderData: (prev: any) => prev,
  });
  const logs = data?.items ?? [];
  const total = data?.total ?? 0;

  const applySearch = () => { setErrorQuery(errorMessage.trim()); setPage(1); };
  const reset = () => {
    setCourierId(''); setAction(''); setEntityId('');
    setErrorMessage(''); setErrorQuery(''); setOnlyErrors(''); setPage(1);
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Server size={22} className="text-emerald-700" /> Courier API Logs
          <span className="text-sm font-normal text-slate-500">— {total.toLocaleString()} calls</span>
        </h1>
        <button onClick={() => refetch()} disabled={isFetching} className="px-3 py-2 border rounded text-sm flex items-center gap-1">
          <RefreshCcw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div className="w-64">
          <label className="text-xs text-slate-500 block mb-1">Courier</label>
          <SearchableSelect value={courierId === '' ? '' : courierId}
            onChange={(v) => { setCourierId(v === '' ? '' : Number(v)); setPage(1); }}
            options={(couriers ?? []).map((c) => ({ value: c.id, label: c.name + ' (#' + c.id + ')', hint: c.code }))} placeholder="All couriers" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Action</label>
          <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
            <option value="">All actions</option>
            {actionOpts.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Result</label>
          <select value={onlyErrors} onChange={(e) => { setOnlyErrors(e.target.value as any); setPage(1); }} className="border rounded px-2 py-1.5 text-sm">
            <option value="">All</option>
            <option value="true">Errors only</option>
            <option value="false">Success only</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Warehouse / Entity ID</label>
          <input value={entityId} onChange={(e) => { setEntityId(e.target.value); setPage(1); }} placeholder="e.g. 42" className="border rounded px-2 py-1.5 text-sm w-28" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <label className="text-xs text-slate-500 block mb-1">Search error message</label>
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden">
            <Search size={14} className="text-slate-400 ml-2 shrink-0" />
            <input value={errorMessage} onChange={(e) => setErrorMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
              placeholder="e.g. warehouse not configured, Unauthorized, pincode"
              className="flex-1 px-2 py-1.5 text-sm outline-none min-w-0" />
            {errorMessage && (
              <button onClick={() => { setErrorMessage(''); setErrorQuery(''); setPage(1); }} title="Clear" className="px-2 text-slate-400 hover:text-slate-700">✕</button>
            )}
          </div>
        </div>
        <button onClick={applySearch}
          className="px-3 py-2 border border-emerald-300 text-emerald-700 text-sm rounded-lg hover:bg-emerald-50 flex items-center gap-1.5">
          <Search size={14} /> Search
        </button>
        <button onClick={reset} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Reset</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 text-xs text-slate-500">
          <span>{errorQuery ? <>Matching <b>“{errorQuery}”</b> · {total.toLocaleString()} calls</> : <>{total.toLocaleString()} calls</>}</span>
          <span>Rows:&nbsp;
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="text-xs border border-slate-200 rounded px-1 py-0.5">
              {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Merchant</th>
                <th className="px-3 py-2 text-left">Courier</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Entity</th>
                <th className="px-3 py-2 text-left">HTTP</th>
                <th className="px-3 py-2 text-left">Status / Message</th>
                <th className="px-3 py-2 text-right">Payload</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Loading…</td></tr>}
              {!isLoading && logs.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-slate-400">
                  {errorQuery || action || courierId || entityId || onlyErrors ? 'No courier API calls match these filters.' : 'No courier API calls in window.'}
                </td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className={`border-t hover:bg-slate-50 ${l.isError ? 'bg-rose-50/30' : ''}`}>
                  <td className="px-3 py-1.5 text-xs text-slate-600 whitespace-nowrap">{new Date(l.createdAt).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-1.5 text-xs">{l.merchantName ?? (l.merchantId ? `#${l.merchantId}` : '—')}</td>
                  <td className="px-3 py-1.5 text-xs"><div className="font-medium">{l.courierName ?? '—'}</div><div className="font-mono text-[10px] text-slate-400">{l.courierCode}</div></td>
                  <td className="px-3 py-1.5 text-xs font-mono">{l.action}</td>
                  <td className="px-3 py-1.5 text-xs">{l.entityType && l.entityId ? `${l.entityType}#${l.entityId}` : '—'}</td>
                  <td className="px-3 py-1.5 text-xs">{l.statusCode ?? '—'}</td>
                  <td className={`px-3 py-1.5 text-xs ${l.isError ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {l.isError
                      ? <span className="flex items-start gap-1"><AlertCircle size={12} className="mt-0.5 shrink-0" /><span className="max-w-[380px] line-clamp-2" title={l.errorMessage ?? ''}>{l.errorMessage ?? '(no message)'}</span></span>
                      : <>✓ Success</>}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => setDetail(l)} title="View request &amp; response"
                      className="px-2 py-1 border border-slate-200 rounded text-xs hover:bg-slate-100 inline-flex items-center gap-1 whitespace-nowrap">
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end px-4 py-2 border-t border-slate-100">
          {total > 0 && <GmailPagination page={page} pageSize={pageSize} total={total} onPage={setPage} />}
        </div>
      </div>

      {detail && <PayloadModal log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

/**
 * Request / response viewer. Tabbed (Request · Response · Error), pretty-printed, with copy buttons
 * and word-wrap — the inline expanding row it replaces made long courier payloads unreadable.
 */
function PayloadModal({ log, onClose }: { log: LogRow; onClose: () => void }) {
  const [tab, setTab] = useState<'request' | 'response' | 'error'>(log.isError ? 'error' : 'response');
  const [wrap, setWrap] = useState(true);
  const [copied, setCopied] = useState(false);

  const requestText = pretty(log.requestBody);
  const responseText = pretty(log.responseBody);
  const errorText = [
    log.errorMessage ? `Message:\n${log.errorMessage}` : null,
    log.statusCode != null ? `HTTP status: ${log.statusCode}` : null,
    log.requestUrl ? `URL:\n${log.requestUrl}` : null,
  ].filter(Boolean).join('\n\n') || '(no error details recorded)';

  const body = tab === 'request' ? requestText : tab === 'response' ? responseText : errorText;

  const copy = () => {
    navigator.clipboard.writeText(body).then(() => {
      setCopied(true); toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    }, () => toast.error('Copy failed'));
  };

  const TABS = [
    { id: 'request' as const, label: 'Request' },
    { id: 'response' as const, label: 'Response' },
    ...(log.isError || log.errorMessage ? [{ id: 'error' as const, label: 'Error' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header: what this call was */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm">{log.action}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded ${log.isError ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {log.isError ? 'Failed' : 'Success'}{log.statusCode != null ? ` · HTTP ${log.statusCode}` : ''}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              {log.courierName ?? log.courierCode}
              {log.merchantName ? ` · ${log.merchantName}` : ''}
              {log.entityType && log.entityId ? ` · ${log.entityType}#${log.entityId}` : ''}
              {' · '}{new Date(log.createdAt).toLocaleString('en-IN')}
            </div>
            {log.requestUrl && (
              <div className="text-[11px] font-mono text-slate-400 mt-1 break-all">{log.requestUrl}</div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0"><X size={18} /></button>
        </div>

        {/* Tabs + controls */}
        <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-3 py-1 text-xs rounded-md transition ${tab === t.id ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <label className="text-xs text-slate-500 flex items-center gap-1.5 ml-auto">
            <input type="checkbox" checked={wrap} onChange={(e) => setWrap(e.target.checked)} className="rounded" /> Wrap lines
          </label>
          <button onClick={copy} className="px-2.5 py-1 border border-slate-200 rounded text-xs hover:bg-slate-50 flex items-center gap-1">
            {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />} Copy
          </button>
        </div>

        {/* Payload */}
        <div className="p-5 pt-3 overflow-auto flex-1">
          <pre className={`bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] leading-relaxed font-mono overflow-x-auto ${wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'}`}>{body}</pre>
        </div>
      </div>
    </div>
  );
}
